import { useState, useEffect, useRef } from "react";
import { MapLocation } from "@/types/location";
import { LocationSelectionManager } from "@/components/map/LocationSelectionManager";

export const useLocationSelection = (
  filteredLocations: MapLocation[],
  locations: MapLocation[],
  onDeviceSelect: (devices: number) => void,
  onLocationsChange: (locations: Array<{ name: string, devices: number }>) => void,
  activeMediaOwner: string,
  initialSelectedLocations?: Set<number>,
  attentionScreenCounts?: Map<number, number>
) => {
  const [selectedLocations, setSelectedLocations] = useState<Set<number>>(
    initialSelectedLocations || new Set()
  );
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());

  // Keep latest selection in a ref to avoid stale closures during rapid interactions
  const selectedLocationsRef = useRef<Set<number>>(initialSelectedLocations || new Set());
  useEffect(() => {
    selectedLocationsRef.current = selectedLocations;
  }, [selectedLocations]);

  // Keep latest attentionScreenCounts in a ref
  const attentionScreenCountsRef = useRef<Map<number, number>>(attentionScreenCounts || new Map());
  useEffect(() => {
    attentionScreenCountsRef.current = attentionScreenCounts || new Map();
  }, [attentionScreenCounts]);

  // Per-id debounce guard to prevent double-trigger glitches
  const lastActionRef = useRef<Map<number, number>>(new Map());

  // Initialize from imported locations when both imported locations and locations data are available
  const didInitRef = useRef(false);
  const locationsWereEmptyRef = useRef(true);

  // Reset initialization flag if locations were empty but are now available
  useEffect(() => {
    const locationsAreEmpty = locations.length === 0;
    const locationsWereEmpty = locationsWereEmptyRef.current;
    if (locationsWereEmpty && !locationsAreEmpty) {
      didInitRef.current = false;
    }
    locationsWereEmptyRef.current = locationsAreEmpty;
  }, [locations.length]);

  useEffect(() => {
    if (didInitRef.current) return;
    if (initialSelectedLocations && initialSelectedLocations.size > 0 && locations.length > 0) {
      const initSet = new Set(initialSelectedLocations);
      setSelectedLocations(initSet);
      LocationSelectionManager.updateSelections(
        initSet,
        onDeviceSelect,
        onLocationsChange,
        locations,
        attentionScreenCounts
      );
      didInitRef.current = true;
    } else if (initialSelectedLocations && initialSelectedLocations.size === 0) {
      didInitRef.current = true;
    }
  }, [initialSelectedLocations, locations, onDeviceSelect, onLocationsChange, attentionScreenCounts]);

  // Sync imported selection updates after initialization (e.g., AI apply)
  useEffect(() => {
    if (!initialSelectedLocations || locations.length === 0) return;
    const curr = selectedLocationsRef.current;

    if (initialSelectedLocations.size === 0 && curr.size > 0) {
      const next = new Set<number>();
      setSelectedLocations(next);
      LocationSelectionManager.updateSelections(next, onDeviceSelect, onLocationsChange, locations, attentionScreenCounts);
      return;
    }

    let shouldApply = false;
    if (curr.size === 0 && initialSelectedLocations.size > 0) shouldApply = true;
    else {
      for (const id of Array.from(initialSelectedLocations)) {
        if (!curr.has(id)) { shouldApply = true; break; }
      }
    }
    if (!shouldApply) return;

    const next = new Set(initialSelectedLocations);
    setSelectedLocations(next);
    LocationSelectionManager.updateSelections(
      next,
      onDeviceSelect,
      onLocationsChange,
      locations,
      attentionScreenCounts
    );
  }, [initialSelectedLocations, locations, onDeviceSelect, onLocationsChange, attentionScreenCounts]);

  const handleToggleAllInRegion = (region: string) => {
    const newSelectedLocations = LocationSelectionManager.toggleAllInRegion(
      region,
      selectedLocations,
      filteredLocations,
      activeMediaOwner
    );
    setSelectedLocations(newSelectedLocations);
    LocationSelectionManager.updateSelections(
      newSelectedLocations,
      onDeviceSelect,
      onLocationsChange,
      locations,
      attentionScreenCountsRef.current
    );
  };

  const handleToggleTypeInRegion = (region: string, type: string) => {
    const newSelectedLocations = LocationSelectionManager.toggleTypeInRegion(
      region,
      type,
      selectedLocations,
      setSelectedTypes,
      filteredLocations,
      activeMediaOwner
    );
    setSelectedLocations(newSelectedLocations);
    LocationSelectionManager.updateSelections(
      newSelectedLocations,
      onDeviceSelect,
      onLocationsChange,
      locations,
      attentionScreenCountsRef.current
    );
  };

  const handleDeselectLocation = (id: number) => {
    const now = Date.now();
    const last = lastActionRef.current.get(id) || 0;
    if (now - last < 250) return;
    lastActionRef.current.set(id, now);

    const prev = selectedLocationsRef.current;
    if (!prev.has(id)) return;

    const next = new Set(prev);
    next.delete(id);

    setSelectedLocations(next);
    LocationSelectionManager.updateSelections(
      next,
      onDeviceSelect,
      onLocationsChange,
      locations,
      attentionScreenCountsRef.current
    );
  };

  const handleLocationSelect = (id: number) => {
    const now = Date.now();
    const last = lastActionRef.current.get(id) || 0;
    if (now - last < 250) return;
    lastActionRef.current.set(id, now);

    const prev = selectedLocationsRef.current;
    const next = new Set(prev);
    if (next.has(id)) {
      next.delete(id);
    } else {
      // Prevent selecting maintenance/offline locations
      const loc = locations.find(l => l.id === id);
      if (loc && (loc.maintenanceStatus === 'maintenance' || loc.maintenanceStatus === 'offline')) {
        return;
      }
      next.add(id);
    }

    setSelectedLocations(next);
    LocationSelectionManager.updateSelections(
      next,
      onDeviceSelect,
      onLocationsChange,
      locations,
      attentionScreenCountsRef.current
    );
  };

  const refreshSelections = () => {
    LocationSelectionManager.updateSelections(
      selectedLocationsRef.current,
      onDeviceSelect,
      onLocationsChange,
      locations,
      attentionScreenCountsRef.current
    );
  };

  return {
    selectedLocations,
    handleToggleAllInRegion,
    handleToggleTypeInRegion,
    handleDeselectLocation,
    handleLocationSelect,
    refreshSelections,
    isRemoving: false
  };
};
